import * as THREE from "three";

const SIZE = 512;
const cache = new Map();

const rng = (seed) => () => {
  seed = (seed * 16807) % 2147483647;
  return (seed - 1) / 2147483646;
};

const gray = (v, a = 1) => {
  const c = Math.round(Math.max(0, Math.min(1, v)) * 255);
  return `rgba(${c},${c},${c},${a})`;
};

const speckle = (ctx, rnd, count, min, max, alpha, size = 1.5) => {
  for (let i = 0; i < count; i += 1) {
    ctx.fillStyle = gray(min + rnd() * (max - min), alpha);
    ctx.fillRect(rnd() * SIZE, rnd() * SIZE, size, size);
  }
};

const grain = (ctx, rnd, x0, w, base) => {
  for (let i = 0; i < w / 2.2; i += 1) {
    const x = x0 + rnd() * w;
    const amp = 2 + rnd() * 6;
    const freq = 0.004 + rnd() * 0.012;
    const ph = rnd() * 10;
    ctx.strokeStyle = gray(base - 0.08 - rnd() * 0.14, 0.35 + rnd() * 0.35);
    ctx.lineWidth = 0.6 + rnd() * 1.4;
    ctx.beginPath();
    for (let y = 0; y <= SIZE; y += 8) {
      const xx = x + Math.sin(y * freq + ph) * amp;
      if (y === 0) ctx.moveTo(xx, y); else ctx.lineTo(xx, y);
    }
    ctx.stroke();
  }
};

const knot = (ctx, rnd, base) => {
  const x = rnd() * SIZE;
  const y = rnd() * SIZE;
  for (let r = 14; r > 2; r -= 3) {
    ctx.strokeStyle = gray(base - 0.25, 0.35);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(x, y, r * 0.6, r * 1.6, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
};

const DRAW = {
  wood(ctx, rnd) {
    ctx.fillStyle = gray(0.9);
    ctx.fillRect(0, 0, SIZE, SIZE);
    grain(ctx, rnd, 0, SIZE, 0.9);
    if (rnd() > 0.3) knot(ctx, rnd, 0.9);
    speckle(ctx, rnd, 2500, 0.7, 1, 0.12);
  },
  planks(ctx, rnd) {
    const n = 8;
    const w = SIZE / n;
    for (let i = 0; i < n; i += 1) {
      const base = 0.78 + rnd() * 0.18;
      const cut = rnd() * SIZE;
      [[0, cut], [cut, SIZE]].forEach(([a, b], k) => {
        const tone = k ? base - 0.04 + rnd() * 0.08 : base;
        ctx.fillStyle = gray(tone);
        ctx.fillRect(i * w, a, w, b - a);
      });
      ctx.save();
      ctx.beginPath();
      ctx.rect(i * w, 0, w, SIZE);
      ctx.clip();
      grain(ctx, rnd, i * w, w, base);
      if (rnd() > 0.6) knot(ctx, rnd, base);
      ctx.restore();
      ctx.fillStyle = gray(0.35, 0.9);
      ctx.fillRect(i * w, 0, 2, SIZE);
      ctx.fillRect(i * w, cut, w, 2);
    }
    speckle(ctx, rnd, 4000, 0.6, 1, 0.1);
  },
  plaster(ctx, rnd) {
    ctx.fillStyle = gray(0.94);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 90; i += 1) {
      const g = ctx.createRadialGradient(rnd() * SIZE, rnd() * SIZE, 0, 0, 0, 0);
      const x = rnd() * SIZE;
      const y = rnd() * SIZE;
      const r = 20 + rnd() * 70;
      const rg = ctx.createRadialGradient(x, y, 0, x, y, r);
      rg.addColorStop(0, gray(0.86 + rnd() * 0.14, 0.35));
      rg.addColorStop(1, gray(0.94, 0));
      ctx.fillStyle = rg || g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    speckle(ctx, rnd, 14000, 0.78, 1, 0.25, 1.2);
  },
  fabric(ctx, rnd) {
    ctx.fillStyle = gray(0.9);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < SIZE; i += 4) {
      ctx.fillStyle = gray(0.78 + rnd() * 0.12, 0.6);
      ctx.fillRect(0, i, SIZE, 1.5);
      ctx.fillStyle = gray(0.8 + rnd() * 0.12, 0.5);
      ctx.fillRect(i + 2, 0, 1.5, SIZE);
    }
    speckle(ctx, rnd, 6000, 0.7, 1, 0.2);
  },
  rug(ctx, rnd) {
    ctx.fillStyle = gray(0.9);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let y = 0; y < SIZE; y += 3) {
      for (let x = 0; x < SIZE; x += 3) {
        ctx.fillStyle = gray(0.76 + rnd() * 0.24, 0.8);
        ctx.fillRect(x, y, 2.4, 2.4);
      }
    }
  },
  leather(ctx, rnd) {
    ctx.fillStyle = gray(0.85);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 2200; i += 1) {
      const x = rnd() * SIZE;
      const y = rnd() * SIZE;
      ctx.fillStyle = gray(0.7 + rnd() * 0.3, 0.35);
      ctx.beginPath();
      ctx.arc(x, y, 2 + rnd() * 5, 0, Math.PI * 2);
      ctx.fill();
    }
    speckle(ctx, rnd, 6000, 0.6, 1, 0.2);
  },
  paper(ctx, rnd) {
    ctx.fillStyle = gray(0.97);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 400; i += 1) {
      ctx.strokeStyle = gray(0.85 + rnd() * 0.1, 0.25);
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      const x = rnd() * SIZE;
      const y = rnd() * SIZE;
      ctx.moveTo(x, y);
      ctx.lineTo(x + (rnd() - 0.5) * 30, y + (rnd() - 0.5) * 30);
      ctx.stroke();
    }
    speckle(ctx, rnd, 5000, 0.85, 1, 0.3);
  },
  canvas(ctx, rnd) {
    DRAW.fabric(ctx, rnd);
    for (let i = 0; i < 160; i += 1) {
      ctx.strokeStyle = gray(0.75 + rnd() * 0.25, 0.25);
      ctx.lineWidth = 3 + rnd() * 8;
      ctx.beginPath();
      const x = rnd() * SIZE;
      const y = rnd() * SIZE;
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + 30, y + (rnd() - 0.5) * 40, x + 40 + rnd() * 60, y + (rnd() - 0.5) * 20);
      ctx.stroke();
    }
  },
  globe(ctx, rnd) {
    ctx.fillStyle = "#3E6E7E";
    ctx.fillRect(0, 0, SIZE, SIZE);
    speckle(ctx, rnd, 6000, 0.2, 0.5, 0.08);
    const blobs = [[0.18, 0.35, 0.09], [0.22, 0.62, 0.07], [0.48, 0.32, 0.08], [0.52, 0.55, 0.09], [0.72, 0.3, 0.12], [0.82, 0.62, 0.06], [0.3, 0.85, 0.05]];
    blobs.forEach(([bx, by, br]) => {
      for (let i = 0; i < 26; i += 1) {
        ctx.fillStyle = rnd() > 0.5 ? "#C9B48A" : "#A9B98A";
        ctx.beginPath();
        ctx.ellipse((bx + (rnd() - 0.5) * br * 1.6) * SIZE, (by + (rnd() - 0.5) * br * 1.6) * SIZE, br * SIZE * (0.25 + rnd() * 0.35), br * SIZE * (0.2 + rnd() * 0.3), rnd() * 3, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    ctx.strokeStyle = "rgba(247,244,238,0.25)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 12; i += 1) {
      ctx.beginPath(); ctx.moveTo((i / 12) * SIZE, 0); ctx.lineTo((i / 12) * SIZE, SIZE); ctx.stroke();
    }
    for (let i = 1; i < 6; i += 1) {
      ctx.beginPath(); ctx.moveTo(0, (i / 6) * SIZE); ctx.lineTo(SIZE, (i / 6) * SIZE); ctx.stroke();
    }
  },
  brushed(ctx, rnd) {
    ctx.fillStyle = gray(0.88);
    ctx.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 1400; i += 1) {
      ctx.fillStyle = gray(0.75 + rnd() * 0.25, 0.3);
      ctx.fillRect(rnd() * SIZE, rnd() * SIZE, 40 + rnd() * 160, 0.8);
    }
  },
  plastic(ctx, rnd) {
    ctx.fillStyle = gray(0.95);
    ctx.fillRect(0, 0, SIZE, SIZE);
    speckle(ctx, rnd, 9000, 0.82, 1, 0.25, 1.2);
  },
};

export const getTexture = (type, repeat = [1, 1]) => {
  if (!type || !DRAW[type]) return null;
  const base = `${type}`;
  if (!cache.has(base)) {
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d");
    DRAW[type](ctx, rng(type.length * 7919 + 13));
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = 8;
    cache.set(base, tex);
  }
  const key = `${type}|${repeat[0]}|${repeat[1]}`;
  if (!cache.has(key)) {
    const t = cache.get(base).clone();
    t.repeat.set(repeat[0], repeat[1]);
    t.needsUpdate = true;
    cache.set(key, t);
  }
  return cache.get(key);
};

export const BUMP = { wood: 1.2, planks: 2, plaster: 1.5, fabric: 2.5, rug: 3, leather: 2, paper: 0.6, canvas: 3, brushed: 0.6, plastic: 0.4, globe: 0.3 };
